import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useCallback } from 'react'
import { Card, Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { DocumentFilters } from '../site/api'
import { SiteContent, SiteResyncButton } from '../site/components/site-content'
import { SiteContentSummary } from '../site/components/site-overview'
import { SiteAnswers } from '../site/components/site-answers'
import { SiteCrawl } from '../site/components/site-crawl'
import { useCanMarkVacancies, useShowsVacancySwitch } from '../site/components/site-crawl-rules'
import { useSite } from '../site/hooks'

// Everything the assistant knows: the pages Evaa read on the website, and the answers the company added.
export const Route = createFileRoute('/_auth/_main/websites/$siteId/settings/knowledge')({
    validateSearch: z.object({
        status: z.string().optional(),
        kind: z.string().optional(),
        search: z.string().optional(),
        page: z.number().optional(),
    }),
    component: Knowledge,
})

function Knowledge() {
    const { siteId } = Route.useParams()
    const { t } = useTranslation()
    const filters = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const { data: site } = useSite(siteId)
    const snippet = site?.type === 'snippet'
    const canMarkVacancies = useCanMarkVacancies(siteId)
    const vacancySwitch = useShowsVacancySwitch(siteId)
    const hasNoVacancies = site !== undefined && (site.vacanciesCount ?? 0) === 0

    // Before the Vacancy column shows, a page is marked as a vacancy from its menu.
    const marksVacanciesFromMenu = canMarkVacancies && !vacancySwitch

    const onFiltersChange = useCallback(
        (next: DocumentFilters) =>
            navigate({
                search: next,
                replace: true,
            }),
        [navigate]
    )

    const showStatus = (status: string) =>
        onFiltersChange({
            ...filters,
            status,
            page: 1,
        })

    return (
        <Flex
            vertical
            gap="large"
        >
            <Card
                title={t('Pages of the website')}
                extra={
                    <SiteResyncButton
                        siteId={siteId}
                        snippet={snippet}
                    />
                }
            >
                <Flex
                    vertical
                    gap="middle"
                >
                    <SiteContentSummary
                        siteId={siteId}
                        onShowFailed={() => showStatus('failed')}
                        onShowNeedsJavascript={() => showStatus('needs_javascript')}
                        onShowSkipped={() => showStatus('skipped')}
                        onShowLimitReached={() => showStatus('limit_reached')}
                    />
                    {hasNoVacancies && (
                        <Typography.Text type="secondary">
                            {t(
                                'When Evaa finds vacancies on your website, Vacancies, Apply form and WhatsApp appear in the menu too.'
                            )}
                            {marksVacanciesFromMenu && ` ${t('Is a vacancy below as a page? Mark it via ⋯.')}`}
                        </Typography.Text>
                    )}
                    <SiteContent
                        siteId={siteId}
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                    />
                </Flex>
            </Card>
            <SiteAnswers siteId={siteId} />
            {snippet && <SiteCrawl siteId={siteId} />}
        </Flex>
    )
}
